import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getTransactionHistory, Transaction } from '../services/creditsService';
import { formatBtcAmount } from '../services/bitcoinService';
import DownloadFilesModal from '../components/DownloadFilesModal';
import { isAuthenticated } from '../services/authService';

interface Order {
  id: string;
  date: string;
  status: 'completed' | 'processing' | 'refunded' | 'pending';
  items: {
    id: number;
    title: string;
    creator: string;
    price: number;
    quantity: number;
    imageUrl?: string;
    releaseDate?: string;
  }[];
  total: number;
  paymentMethod: string;
}

// Conversion from transactions to orders
const convertTransactionsToOrders = (transactions: Transaction[]): Order[] => {
  // Only include purchase transactions
  const purchaseTransactions = transactions.filter(t => t.type === 'Purchase');

  if (purchaseTransactions.length === 0) return [];

  // Group transactions by date (simple grouping for demo)
  const ordersByDate = purchaseTransactions.reduce((acc, t) => {
    const date = t.date;
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(t);
    return acc;
  }, {} as Record<string, Transaction[]>);

  // Convert grouped transactions to orders
  return Object.entries(ordersByDate).map(([date, transactions], index) => {
    const total = transactions.reduce((sum, t) => sum + Math.abs(t.amount), 0);

    return {
      id: `ORD-${date.replace(/\s/g, '-')}-${index + 1}`,
      date,
      status: 'completed',
      items: transactions.map((t, idx) => ({
        id: idx + 1,
        title: t.itemName || 'Product Purchase',
        creator: t.sellerId || 'Unknown Seller',
        price: Math.abs(t.amount),
        quantity: 1,
        imageUrl: `https://via.placeholder.com/300x200/7F56D9/FFFFFF?text=Product+${idx + 1}`,
      })),
      total,
      paymentMethod: 'Credits',
    };
  });
};

const OrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [orders, setOrders] = useState<Order[]>([]);
  const [btcPrice] = useState<number>(60000); // Default BTC price
  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [currentOrderId, setCurrentOrderId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedOrders, setExpandedOrders] = useState<Record<string, boolean>>({});
  const [sortBy, setSortBy] = useState<'date' | 'total'>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [timeFilter, setTimeFilter] = useState<'all' | 'month' | 'quarter' | 'year'>('all');
  const [isLoading, setIsLoading] = useState(true);

  const ordersPerPage = 5;

  // Check authentication before loading any data
  useEffect(() => {
    // Check if user is authenticated
    if (!isAuthenticated()) {
      navigate('/login', { state: { from: location } });
      return;
    }

    setIsLoading(false);
  }, [navigate, location]);

  // Fetch transaction data
  useEffect(() => {
    if (isLoading) return; // Don't fetch data if still checking authentication

    const fetchTransactions = async () => {
      try {
        const transactions = await getTransactionHistory();
        const convertedOrders = convertTransactionsToOrders(transactions);

        // Add mock orders for demonstration if no real orders exist
        if (convertedOrders.length === 0) {
          // Generate a larger set of sample orders for pagination demo
          const sampleOrders: Order[] = [];

          // Current orders
          sampleOrders.push({
            id: 'ORD-2023-06-14',
            date: 'Jun 14, 2023',
            status: 'completed',
            items: [
              {
                id: 1,
                title: 'Premium UI Kit for Dashboard',
                creator: 'DesignMaster',
                price: 49.99,
                quantity: 1,
                imageUrl: 'https://via.placeholder.com/300x200/7F56D9/FFFFFF?text=UI+Kit',
              },
              {
                id: 2,
                title: 'Advanced Analytics Dashboard Template',
                creator: 'DataVizPro',
                price: 39.99,
                quantity: 1,
                imageUrl: 'https://via.placeholder.com/300x200/F670C7/FFFFFF?text=Dashboard',
              }
            ],
            total: 89.98,
            paymentMethod: 'Credits',
          });

          sampleOrders.push({
            id: 'ORD-2023-05-29',
            date: 'May 29, 2023',
            status: 'pending',
            items: [
              {
                id: 3,
                title: 'E-commerce API Integration Kit',
                creator: 'APIWizard',
                price: 79.99,
                quantity: 1,
                imageUrl: 'https://via.placeholder.com/300x200/7F56D9/FFFFFF?text=API+Kit',
                releaseDate: 'Jun 5, 2023',
              }
            ],
            total: 79.99,
            paymentMethod: 'Credits (Pending release to seller)',
          });

          // Add more sample orders with different dates and statuses
          const statuses: ('completed' | 'processing' | 'refunded' | 'pending')[] = ['completed', 'processing', 'refunded', 'pending'];

          // Generate orders for the past year with different statuses
          for (let i = 1; i <= 15; i++) {
            const date = new Date();
            date.setMonth(date.getMonth() - i);

            sampleOrders.push({
              id: `ORD-${date.getFullYear()}-${date.getMonth() + 1}-${i}`,
              date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
              status: statuses[i % statuses.length],
              items: [
                {
                  id: i + 10,
                  title: `Product ${i}`,
                  creator: `Creator ${i}`,
                  price: 19.99 * (i % 5 + 1),
                  quantity: 1,
                  imageUrl: `https://via.placeholder.com/300x200/7F56D9/FFFFFF?text=Product+${i}`,
                }
              ],
              total: 19.99 * (i % 5 + 1),
              paymentMethod: 'Credits',
            });
          }

          setOrders(sampleOrders);
        } else {
          setOrders(convertedOrders);
        }
      } catch (error) {
        console.error('Error fetching orders:', error);
        // Handle error - maybe show error state
      }
    };

    fetchTransactions();
  }, [isLoading]);

  // Initialize all orders as collapsed
  useEffect(() => {
    const initialExpandState: Record<string, boolean> = {};
    orders.forEach(order => {
      initialExpandState[order.id] = false;
    });
    setExpandedOrders(initialExpandState);
  }, [orders]);

  const handleDownloadClick = (orderId: string) => {
    setCurrentOrderId(orderId);
    setShowDownloadModal(true);
  };

  const toggleOrderExpanded = (orderId: string) => {
    setExpandedOrders(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500/20 text-green-500';
      case 'processing':
        return 'bg-blue-500/20 text-blue-500';
      case 'refunded':
        return 'bg-red-500/20 text-red-500';
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-500';
      default:
        return 'bg-gray-500/20 text-gray-500';
    }
  };

  const formatDate = (dateString: string) => {
    // Check if the date is already formatted
    if (dateString.includes(',')) return dateString;

    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  // Calculate BTC equivalent
  const getBtcEquivalent = (amount: number): string => {
    return formatBtcAmount(amount / btcPrice);
  };

  // Apply filters, search, and sorting
  const filteredAndSortedOrders = useMemo(() => {
    let result = [...orders];

    // Apply status filter
    if (selectedFilter !== 'all') {
      result = result.filter(order => order.status === selectedFilter);
    }

    // Apply time filter
    if (timeFilter !== 'all') {
      const now = new Date();
      const cutoffDate = new Date();

      switch (timeFilter) {
        case 'month':
          cutoffDate.setMonth(now.getMonth() - 1);
          break;
        case 'quarter':
          cutoffDate.setMonth(now.getMonth() - 3);
          break;
        case 'year':
          cutoffDate.setFullYear(now.getFullYear() - 1);
          break;
      }

      result = result.filter(order => {
        const orderDate = new Date(order.date);
        return orderDate >= cutoffDate;
      });
    }

    // Apply search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(order =>
        order.id.toLowerCase().includes(query) ||
        order.items.some(item =>
          item.title.toLowerCase().includes(query) ||
          item.creator.toLowerCase().includes(query)
        )
      );
    }

    // Apply sorting
    result.sort((a, b) => {
      if (sortBy === 'date') {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return sortDirection === 'asc'
          ? dateA.getTime() - dateB.getTime()
          : dateB.getTime() - dateA.getTime();
      } else {
        return sortDirection === 'asc'
          ? a.total - b.total
          : b.total - a.total;
      }
    });

    return result;
  }, [orders, selectedFilter, searchQuery, sortBy, sortDirection, timeFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedOrders.length / ordersPerPage);
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = filteredAndSortedOrders.slice(indexOfFirstOrder, indexOfLastOrder);

  // Handle page change
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#7F56D9]"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold">My Orders</h1>
        <p className="text-[var(--color-muted)] mt-2">View and manage your purchase history</p>
      </div>

      {/* Search and filters */}
      <div className="bg-[#1F1A27] rounded-lg p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4 mb-4">
          <div className="flex-grow">
            <div className="relative">
              <input
                type="text"
                placeholder="Search orders by ID, product, or creator..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1); // Reset to first page on search
                }}
                className="w-full py-2 px-4 pl-10 bg-[#2A2633] border border-[#3A3643] rounded-md focus:outline-none focus:border-[#7F56D9] text-white"
              />
              <svg
                className="absolute left-3 top-2.5 text-[var(--color-muted)]"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </div>

          <div className="flex gap-2">
            <select
              value={timeFilter}
              onChange={(e) => {
                setTimeFilter(e.target.value as 'all' | 'month' | 'quarter' | 'year');
                setCurrentPage(1);
              }}
              className="bg-[#2A2633] border border-[#3A3643] rounded-md focus:outline-none focus:border-[#7F56D9] text-white py-2 px-3 text-sm"
            >
              <option value="all">All Time</option>
              <option value="month">Last Month</option>
              <option value="quarter">Last 3 Months</option>
              <option value="year">Last Year</option>
            </select>

            <select
              value={sortBy + '-' + sortDirection}
              onChange={(e) => {
                const [newSortBy, newSortDirection] = e.target.value.split('-') as ['date' | 'total', 'asc' | 'desc'];
                setSortBy(newSortBy);
                setSortDirection(newSortDirection);
              }}
              className="bg-[#2A2633] border border-[#3A3643] rounded-md focus:outline-none focus:border-[#7F56D9] text-white py-2 px-3 text-sm"
            >
              <option value="date-desc">Newest First</option>
              <option value="date-asc">Oldest First</option>
              <option value="total-desc">Price: High to Low</option>
              <option value="total-asc">Price: Low to High</option>
            </select>
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          <button
            onClick={() => {
              setSelectedFilter('all');
              setCurrentPage(1);
            }}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              selectedFilter === 'all'
                ? 'bg-[#7F56D9]/20 text-[#7F56D9]'
                : 'bg-[#2A2633] text-[var(--color-muted)] hover:text-white'
            }`}
          >
            All Orders
          </button>
          <button
            onClick={() => {
              setSelectedFilter('completed');
              setCurrentPage(1);
            }}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              selectedFilter === 'completed'
                ? 'bg-green-500/20 text-green-500'
                : 'bg-[#2A2633] text-[var(--color-muted)] hover:text-white'
            }`}
          >
            Completed
          </button>
          <button
            onClick={() => {
              setSelectedFilter('pending');
              setCurrentPage(1);
            }}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              selectedFilter === 'pending'
                ? 'bg-yellow-500/20 text-yellow-500'
                : 'bg-[#2A2633] text-[var(--color-muted)] hover:text-white'
            }`}
          >
            Pending
          </button>
          <button
            onClick={() => {
              setSelectedFilter('processing');
              setCurrentPage(1);
            }}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              selectedFilter === 'processing'
                ? 'bg-blue-500/20 text-blue-500'
                : 'bg-[#2A2633] text-[var(--color-muted)] hover:text-white'
            }`}
          >
            Processing
          </button>
          <button
            onClick={() => {
              setSelectedFilter('refunded');
              setCurrentPage(1);
            }}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              selectedFilter === 'refunded'
                ? 'bg-red-500/20 text-red-500'
                : 'bg-[#2A2633] text-[var(--color-muted)] hover:text-white'
            }`}
          >
            Refunded
          </button>
        </div>
      </div>

      {/* Results summary & pagination info */}
      {filteredAndSortedOrders.length > 0 && (
        <div className="flex justify-between items-center mb-4 text-sm text-[var(--color-muted)]">
          <div>
            Showing {indexOfFirstOrder + 1} to {Math.min(indexOfLastOrder, filteredAndSortedOrders.length)} of {filteredAndSortedOrders.length} orders
          </div>
          <div>
            Page {currentPage} of {totalPages}
          </div>
        </div>
      )}

      {currentOrders.length > 0 ? (
        <div className="space-y-4">
          {currentOrders.map((order) => (
            <div key={order.id} className="bg-[#1F1A27] rounded-lg overflow-hidden shadow-md">
              {/* Order Header - Always visible */}
              <div
                className="bg-[#2A2633] p-4 flex flex-col md:flex-row justify-between md:items-center gap-4 cursor-pointer"
                onClick={() => toggleOrderExpanded(order.id)}
              >
                <div className="flex items-center justify-between w-full">
                  <div className="flex flex-col md:flex-row md:items-center gap-2">
                    <div className="flex items-center">
                      <h3 className="font-medium">Order #{order.id}</h3>
                      <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${getStatusClass(order.status)}`}>
                        {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                      </span>
                    </div>
                    <p className="text-sm text-[var(--color-muted)] md:ml-2">
                      {formatDate(order.date)}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="text-right">
                      <p className="font-medium">{order.total.toFixed(0)} Credits</p>
                      <p className="text-xs text-[#F7931A]">≈ ₿ {getBtcEquivalent(order.total)}</p>
                    </div>
                    <svg
                      className={`transition-transform ${expandedOrders[order.id] ? 'rotate-180' : ''}`}
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M6 9L12 15L18 9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Order Details - Collapsible */}
              {expandedOrders[order.id] && (
                <div className="p-4">
                  <div className="divide-y divide-white/5">
                    {order.items.map((item) => (
                      <div key={item.id} className="py-4 flex gap-4">
                        <div className="flex-shrink-0 w-16 h-16 rounded overflow-hidden bg-[#2A2633]">
                          {item.imageUrl ? (
                            <img src={item.imageUrl} alt={item.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-[#7F56D9]">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                <path d="M19 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19ZM13.96 12.29L11.21 15.83L9.25 13.47L6.5 17H17.5L13.96 12.29Z" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="flex-grow">
                          <Link to={`/product/${item.id}`} className="font-medium hover:text-[#7F56D9] transition-colors">
                            {item.title}
                          </Link>
                          <p className="text-sm text-[var(--color-muted)]">by {item.creator}</p>
                          <div className="flex justify-between items-center mt-2">
                            <p className="text-sm">Qty: {item.quantity}</p>
                            <div>
                              <p className="font-medium">{item.price.toFixed(0)} Credits</p>
                              <p className="text-xs text-[#F7931A]">≈ ₿ {getBtcEquivalent(item.price)}</p>
                            </div>
                          </div>
                          {item.releaseDate && (
                            <div className="mt-1 text-xs text-amber-400">
                              Seller payment will be released on {item.releaseDate}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Order Summary */}
                  <div className="border-t border-white/10 mt-4 pt-4">
                    <div className="flex justify-between">
                      <p className="text-[var(--color-muted)]">Payment Method:</p>
                      <p>{order.paymentMethod}</p>
                    </div>
                    <div className="flex justify-between mt-2">
                      <p className="font-medium">Total:</p>
                      <div className="text-right">
                        <p className="font-medium">{order.total.toFixed(0)} Credits</p>
                        <p className="text-xs text-[#F7931A]">≈ ₿ {getBtcEquivalent(order.total)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="mt-6 flex flex-wrap gap-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadClick(order.id);
                      }}
                      className="bg-[#7F56D9] text-white py-2 px-4 rounded-md text-sm hover:bg-[#7F56D9]/90 transition-colors"
                    >
                      Download Files
                    </button>
                    <button
                      className="text-sm text-[#7F56D9] hover:underline py-2 px-4"
                    >
                      Download Invoice
                    </button>
                    {order.status === 'pending' && (
                      <button className="bg-amber-500 text-white py-2 px-4 rounded-md text-sm hover:bg-amber-600 transition-colors">
                        Request Refund
                      </button>
                    )}
                    <button
                      className="text-sm text-[var(--color-muted)] hover:text-white ml-auto py-2 px-4"
                    >
                      Get Support
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-[#1F1A27] rounded-lg py-16 px-4 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#2A2633] flex items-center justify-center">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-[#7F56D9]" xmlns="http://www.w3.org/2000/svg">
              <path d="M22 12H2M16 6L22 12L16 18" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          {searchQuery ? (
            <>
              <h2 className="text-xl font-semibold mb-2">No matching orders found</h2>
              <p className="text-[var(--color-muted)] max-w-md mx-auto mb-6">
                Try adjusting your search or filter criteria
              </p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedFilter('all');
                  setTimeFilter('all');
                }}
                className="inline-block bg-[#7F56D9] text-white py-2 px-6 rounded-md hover:bg-[#7F56D9]/90 transition-colors"
              >
                Clear Filters
              </button>
            </>
          ) : (
            <>
              <h2 className="text-xl font-semibold mb-2">No orders yet</h2>
              <p className="text-[var(--color-muted)] max-w-md mx-auto mb-6">Browse our marketplace to find digital assets for your next project</p>
              <Link to="/" className="inline-block bg-[#7F56D9] text-white py-2 px-6 rounded-md hover:bg-[#7F56D9]/90 transition-colors">
                Explore Marketplace
              </Link>
            </>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-8 flex justify-center">
          <div className="flex gap-1">
            <button
              onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className={`px-3 py-2 rounded ${
                currentPage === 1
                  ? 'text-[var(--color-muted)] bg-[#2A2633] cursor-not-allowed'
                  : 'text-white bg-[#2A2633] hover:bg-[#3A3643]'
              }`}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18L9 12L15 6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            {/* First page */}
            {currentPage > 3 && (
              <button
                onClick={() => handlePageChange(1)}
                className={`px-3 py-1 rounded text-white bg-[#2A2633] hover:bg-[#3A3643]`}
              >
                1
              </button>
            )}

            {/* Ellipsis */}
            {currentPage > 4 && (
              <span className="px-3 py-1 text-[var(--color-muted)]">...</span>
            )}

            {/* Page numbers */}
            {Array.from({ length: Math.min(totalPages, 5) }).map((_, i) => {
              let pageNum;

              if (totalPages <= 5) {
                // If we have 5 or fewer pages, show all
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                // If we're near the start
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                // If we're near the end
                pageNum = totalPages - 4 + i;
              } else {
                // We're in the middle
                pageNum = currentPage - 2 + i;
              }

              // Only render if pageNum is valid
              if (pageNum > 0 && pageNum <= totalPages) {
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`w-9 py-1 rounded ${
                      currentPage === pageNum
                        ? 'bg-[#7F56D9] text-white'
                        : 'text-white bg-[#2A2633] hover:bg-[#3A3643]'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              }
              return null;
            })}

            {/* Ellipsis */}
            {currentPage < totalPages - 3 && (
              <span className="px-3 py-1 text-[var(--color-muted)]">...</span>
            )}

            {/* Last page */}
            {currentPage < totalPages - 2 && (
              <button
                onClick={() => handlePageChange(totalPages)}
                className={`px-3 py-1 rounded text-white bg-[#2A2633] hover:bg-[#3A3643]`}
              >
                {totalPages}
              </button>
            )}

            <button
              onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className={`px-3 py-2 rounded ${
                currentPage === totalPages
                  ? 'text-[var(--color-muted)] bg-[#2A2633] cursor-not-allowed'
                  : 'text-white bg-[#2A2633] hover:bg-[#3A3643]'
              }`}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 6L15 12L9 18" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Download Files Modal */}
      <DownloadFilesModal
        orderId={currentOrderId}
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
      />
    </div>
  );
};

export default OrdersPage;
